import React, { useState, useEffect, useRef } from 'react';
import { Server, Cpu, HardDrive, Users, RefreshCw, Wifi, Clock } from 'lucide-react';

const CircularProgress = ({ percentage, size = 56, strokeWidth = 5, color = '#10B981' }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const rafRef = useRef(null);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (animatedValue / 100) * circumference;

  useEffect(() => {
    setAnimatedValue(0);
    const startTime = performance.now();
    const from = 0;
    const to = percentage;

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / 800, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedValue(Math.round(from + (to - from) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [percentage]);

  const getColor = (pct) => {
    if (pct >= 90) return '#EF4444';
    if (pct >= 70) return '#F59E0B';
    return color;
  };

  const activeColor = getColor(percentage);

  return (
    <div className="relative inline-flex items-center justify-center" style={{ color: activeColor }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={activeColor} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'none' }}
        />
      </svg>
      <span className="absolute text-xs font-bold tabular-nums">{animatedValue}%</span>
    </div>
  );
};

const HealthBar = ({ percentage, color = '#3B82F6', height = 'h-2' }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setWidth(percentage), 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  const getColor = (pct) => {
    if (pct >= 90) return 'from-red-500 to-red-400';
    if (pct >= 70) return 'from-yellow-500 to-orange-400';
    return 'from-blue-500 to-blue-400';
  };

  return (
    <div className={`w-full ${height} bg-gray-100 rounded-full overflow-hidden`}>
      <div
        className={`${height} bg-gradient-to-r ${getColor(percentage)} rounded-full transition-all duration-1000 ease-out`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
};

const ServerHealthCard = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchHealth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/server-health', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setHealth(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    intervalRef.current = setInterval(fetchHealth, 10000); // refresh every 10s
    return () => clearInterval(intervalRef.current);
  }, []);

  if (loading) {
    return (
      <div className="card animate-pulse">
        <div className="h-1 w-full bg-gradient-to-r from-cyan-500 to-blue-500" />
        <div className="p-5">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-5 h-5 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-40" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-50 rounded-xl p-4">
                <div className="h-10 w-10 bg-gray-200 rounded-lg mb-3" />
                <div className="h-3 bg-gray-200 rounded w-20 mb-2" />
                <div className="h-6 bg-gray-200 rounded w-16" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="card">
        <div className="h-1 w-full bg-gradient-to-r from-red-500 to-red-400" />
        <div className="p-5 text-center text-red-500">
          <Server className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Gagal memuat info server</p>
          <button onClick={fetchHealth} className="mt-2 text-xs text-blue-600 hover:text-blue-700">Retry</button>
        </div>
      </div>
    );
  }

  if (!health) return null;

  const stats = [
    {
      label: 'RAM',
      value: health.ram.usedFormatted,
      sub: `${health.ram.totalFormatted} total`,
      percentage: health.ram.percentage,
      icon: Server,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      label: 'CPU',
      value: `${health.cpu.usage}%`,
      sub: `${health.cpu.cores} cores • ${health.cpu.speed} MHz`,
      percentage: health.cpu.usage,
      icon: Cpu,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Disk',
      value: health.disk.usedFormatted,
      sub: `${health.disk.totalFormatted} total`,
      percentage: health.disk.percentage,
      icon: HardDrive,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    },
    {
      label: 'Active Users',
      value: health.activeUsers,
      sub: 'Connected now',
      percentage: null,
      icon: Users,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
    },
  ];

  return (
    <div className="card overflow-hidden animate-slide-up">
      {/* Header gradient bar */}
      <div className="h-1 w-full bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500" />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Server className="w-4 h-4 text-cyan-600" />
        
              Server Health
            </h3>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
              Online
            </span>
          </div>
          <div className="flex items-center space-x-3">
            {lastRefresh && (
              <span className="text-[10px] text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {lastRefresh.toLocaleTimeString('id-ID')}
              </span>
            )}
            <button
              onClick={fetchHealth}
              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-lg ${stat.iconBg}`}>
                  <stat.icon className={`w-4 h-4 ${stat.iconColor}`} />
                </div>
                {stat.percentage !== null && (
                  <CircularProgress
                    percentage={stat.percentage}
                    size={44}
                    strokeWidth={4}
                    color={stat.percentage >= 90 ? '#EF4444' : stat.percentage >= 70 ? '#F59E0B' : '#10B981'}
                  />
                )}
                {stat.percentage === null && stat.label === 'Active Users' && (
                  <div className="flex items-center justify-center w-11 h-11">
                    <span className="text-2xl font-extrabold text-orange-600">{stat.value}</span>
                  </div>
                )}
              </div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{stat.label}</p>
              {stat.label !== 'Active Users' && (
                <p className="mt-1 text-lg font-bold text-gray-900">{stat.value}</p>
              )}
              <p className="text-[11px] text-gray-400 mt-0.5">{stat.sub}</p>
              {stat.percentage !== null && (
                <div className="mt-2">
                  <HealthBar percentage={stat.percentage} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* System Info Footer */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-gray-400 border-t border-gray-100 pt-3">
          <span className="flex items-center gap-1">
            <Wifi className="w-3 h-3" />
            {health.hostname}
          </span>
          <span>Node {health.nodeVersion}</span>
          <span>Uptime: {health.uptime}</span>
          <span className="ml-auto">Load: {health.cpu.loadAvg.join(' / ')}</span>
        </div>
      </div>
    </div>
  );
};

export default ServerHealthCard;
